/**
 * User Service
 * 
 * Handles user data management in Firestore.
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

export interface UserData {
  uid: string;
  email: string;
  role: 'admin' | 'user';
  displayName?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateUserData {
  uid: string;
  email: string;
  role: 'admin' | 'user';
  displayName?: string;
}

/**
 * Create or update user document in Firestore
 */
export async function createOrUpdateUser(userData: CreateUserData): Promise<void> {
  const userRef = doc(db, 'users', userData.uid);
  const now = new Date();
  
  const existingDoc = await getDoc(userRef);
  
  if (existingDoc.exists()) {
    // Update existing user
    await updateDoc(userRef, {
      email: userData.email,
      role: userData.role,
      displayName: userData.displayName || null,
      updatedAt: Timestamp.fromDate(now),
    });
  } else {
    // Create new user
    await setDoc(userRef, {
      uid: userData.uid,
      email: userData.email,
      role: userData.role,
      displayName: userData.displayName || null,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    });
  }
}

/**
 * Get user data from Firestore
 */
export async function getUserData(uid: string): Promise<UserData | null> {
  const userRef = doc(db, 'users', uid);
  const userDoc = await getDoc(userRef);
  
  if (!userDoc.exists()) {
    return null;
  }
  
  const data = userDoc.data();
  return {
    uid: data.uid,
    email: data.email,
    role: data.role,
    displayName: data.displayName,
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
  };
}

/**
 * Get user role from Firestore
 */
export async function getUserRole(uid: string): Promise<'admin' | 'user' | null> {
  const userData = await getUserData(uid);
  return userData?.role || null;
}

/**
 * Check if user is admin
 */
export async function isUserAdmin(uid: string): Promise<boolean> {
  const role = await getUserRole(uid);
  return role === 'admin';
}

/**
 * Delete user document from Firestore
 */
export async function deleteUser(uid: string): Promise<void> {
  const userRef = doc(db, 'users', uid);
  await deleteDoc(userRef);
}

/**
 * Get all users
 */
export async function getAllUsers(): Promise<UserData[]> {
  const usersRef = collection(db, 'users');
  const q = query(usersRef);
  const querySnapshot = await getDocs(q);
  
  return querySnapshot.docs.map(doc => {
    const data = doc.data();
    return {
      uid: data.uid,
      email: data.email,
      role: data.role,
      displayName: data.displayName,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
    };
  });
}

export const userService = {
  createOrUpdateUser,
  getUserData,
  getUserRole,
  isUserAdmin,
  deleteUser,
  getAllUsers,
};
